<template>
  <div>
    <v-container fluid>
      <v-row dense class="pb-1">
        <v-col cols="3">
          <date-range :from.sync="from" :to.sync="to" v-model="dateDisplay" @change="refresh()" option="Last 30 days" hide-details/>
        </v-col>
        <v-col cols="2">
          <v-autocomplete label="Show" :items="['bottles','cases','LAA','litres','value']" outlined dense hide-details v-model="show"/>
        </v-col>
        <v-col cols="2">
          <v-autocomplete label="Type" :items="[{text:'All',value:null},{divider:true},{text:'In-bond',value:true},{text:'Duty-paid',value:false}]" outlined dense hide-details v-model="inBond" @change="refresh()"/>
        </v-col>
        <v-col cols="4">
          <inventory-categories-select v-model="categoryId" label="Category" type="Product" none-text="All" @change="refresh()" hide-details/>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml('Product activity', report())"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
      <v-row dense class="pb-0">
        <v-col cols="3">
          <v-autocomplete label="Activity" dense outlined :items="activityTypes" v-model="activityType" hide-details @change="refresh()"/>
        </v-col>
        <v-col cols="3" v-if="sites.length>1">
          <v-autocomplete label="Site" dense outlined hide-details :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="siteId" item-text="name" item-value="id" @change="refresh()"/>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 228px)">
      <span v-html="report()"/>
    </v-card>

  </div>
</template>

<script>
import excise from '@/plugins/excise'
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'
import InventoryCategoriesSelect from '@/views/inventory/categories/InventoryCategoriesSelect'

export default {
  name: 'ReportsProductsActivity',
  mixins: [printHtml],
  components: {InventoryCategoriesSelect},
  data() {
    return {
      from: null,
      to: null,
      dateDisplay: '',
      siteId: null,
      show: 'bottles',
      inBond: true,
      categoryId: null,
      loading: true,
      initial: {},
      total: {},
      transactions: [],
      excise,
      exciseCodes: [],
      activityTypes: [{text:'All',value:null},{divider:true},
                      {text:'Opening inventory',value:'ProductTransactionInitial'},
                      {text:'Bottling',value:'Bottling'},
                      {text:'Move to duty paid',value:'ProductTransactionMoveToDutyPaid'},
                      {text:'Reduce inventory',value:'ProductTransactionReduceInventory'},
                      {text:'Transfer to site',value:'ProductTransactionTransferSite'},
                      {text:'Return to tank',value:'ProductTransactionReturnToTank'},
                      {text:'Shipment',value:'Shipment'},
                      {text:'Inventory receipt',value:'Receipt'},
                      {text:'Product return',value:'ProductReturn'}
                     ],
      activityType: null
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(true);
    }
  },
  async created() {
    this.$emit('name', 'Products activity')
    if (this.$route.query.from && this.$route.query.to) {
      this.from = this.$route.query.from;
      this.to = this.$route.query.to;
    }
    this.exciseCodes = this.excise.productExciseTypes();
    this.inBond = !this.$route.query.dutyPaid;
  },
  methods: {
    async refresh() {
      this.loading = true;
      const results = await this.$apiCall(`query ReportsProductsActivity($from:Date!,$to:Date!,$siteId:ID, $inBond:Boolean, $refType:ObjectType, $categoryId: ID) {
                                                productsBalanceTo(date:$from,siteId:$siteId,inBond:$inBond) {bottles,cases,LAA,litres,value}
                                                productsActivity(from:$from,to:$to,siteId:$siteId,inBond:$inBond,refType:$refType,categoryId:$categoryId) {
                                                  date,description,bottles,cases,litres,laa,value,productName,excise
                                                }
                                              }`,
                                          {from:this.from,to:this.to,siteId:this.siteId,inBond:this.inBond,refType:this.activityType,categoryId:this.categoryId});
      if (!results.error) {
        this.initial = results.data.productsBalanceTo;
        this.transactions = results.data.productsActivity;
        const balance = {...this.initial};
        this.total = {bottles:0, cases:0, LAA: 0, litres:0, value:0};
        for (const transaction of this.transactions) {
          transaction.LAA = transaction.laa;
          this.total.bottles += transaction.bottles;
          this.total.cases += transaction.cases;
          this.total.LAA += transaction.LAA;
          this.total.litres += transaction.litres;
          this.total.value += transaction.value;
          balance.bottles += transaction.bottles;
          balance.cases += transaction.cases;
          balance.litres += transaction.litres;
          balance.value += transaction.value;
          balance.LAA += transaction.LAA;
          transaction.balance = {...balance};
        }
      }
      this.loading = false;
    },

    report() {
    const lines = [
                  `<style>
                    @page {size:landscape}
                    .reports-products-activity {font-family: Roboto, sans-serif; width:100%;border-spacing:0;font-size:12px}
                    .reports-products-activity th {height:36px;padding:0 16px;vertical-align:middle}
                    .reports-products-activity .spacer {height:18px}
                    .reports-products-activity td {height:36px;padding:0 16px;vertical-align:middle}
                    .reports-products-activity .title {font-size:18px;text-transform:uppercase}
                    .reports-products-activity .sup-title {font-weight:normal;font-size:22px}
                    .reports-products-activity .sub-title {font-weight:normal;height:inherit;padding-bottom:20px;font-size:14px}
                    .reports-products-activity .header th {border-bottom:1px solid #000;margin-bottom:20px}
                    .reports-products-activity .text-left {text-align:left}
                    .reports-products-activity .text-right {text-align:right}
                    .reports-products-activity .font-weight-bold {font-weight:bold}
                    .reports-products-activity .total-row {font-weight:bold}
                    .reports-products-activity .capitalize {text-transform: capitalize}
                  </style>
                   <table class="reports-products-activity">
                     <thead>
                        <tr>
                          <th colspan="6" class="sup-title">${this.company.name}</th>
                        </tr>
                        <tr>
                          <th colspan="6" class="title">Products activity</th>
                        </tr>
                        <tr>
                           <th colspan="6" class="sub-title">${this.dateDisplay}</th>
                        </tr>

                        <tr class="header">
                          <th width="${this.activityType?14:10}%" class="text-left">Date</th>
                          <th width="${this.activityType?38:35}%" class="text-left">Activity/Excise</th>
                          <th width="${this.activityType?32:28}%" class="text-left">Product</th>
                          <th width="${this.activityType?15:11}%" class="text-right capitalize">${this.show}</th>
                          ${this.activityType ? '' : '<th width="15%" class="text-right">Balance</th>'}
                        </tr>
                      </thead>
                      <tbody>`
    ];
    if (!this.activityType)
      lines.push(`<tr><td/><td>Balance forward</td><td/><td/><td class="text-right">${this.number(this.initial[this.show])}</td>`);
    for (const transaction of this.transactions) {
      lines.push(`<tr>`);
      lines.push(`<td>${this.$formatDate(transaction.date)}</td>`);
      lines.push(`<td>${transaction.description}<br/>${this.exciseCode(transaction)}</td>`);
      lines.push(`<td>${transaction.productName}</td>`);
      lines.push(`<td class="text-right">${this.number(transaction[this.show])}</td>`);
      if (!this.activityType)
        lines.push(`<td class="text-right">${this.number(transaction.balance[this.show])}</td>`);
      lines.push(`</tr>`);
      lines.push(`<tr><td colspan="5" class="spacer"/></tr>`);
    }
    lines.push(`<tr :class="dark ? 'total-row-dark' : 'total-row'"><td/><td/><td class="text-right">Total</td><td class="text-right">${this.number(this.total[this.show])}</td><td/>`);
    lines.push(`</tbody>`);
    lines.push(`</table>`);
    return lines.join('');
    },
    exciseCode(transaction) {
      const code = this.exciseCodes.find(e => e.value === transaction.excise);
      return code ? code.text : '';
    },
    number(num) {
      if (this.show === 'value')
        return `$${this.$number(num, 2)}`;
      return this.$number(num, this.show === 'bottles' ? 0 : (this.show === 'cases' ? 1 : 3));
    },

    exportCSV() {
      const units = this.show[0].toUpperCase() + this.show.substring(1);
      const lines = [ `"${this.company.name}",,,,`,
                      `"Products activity",,,,`,
                      `"${this.dateDisplay}",,,,`,
                      `"Date","Activity","Product","${units}","${this.activityType ? '' : 'Balance'}"`
                    ];
      if (!this.activityType)
        lines.push(`,"Balance forward",,,"${this.number(this.initial[this.show])}"`);
      for (const transaction of this.transactions) {
        lines.push(`"${transaction.date}","${transaction.description}","${transaction.productName}","${this.number(transaction[this.show])}","${this.activityType?'':this.number(transaction.balance[this.show])}"`);
        lines.push(`,"${this.exciseCode(transaction)}",,,`);
      }
      lines.push(`,"Total",,"${this.number(this.total[this.show])}",`);

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Products activity.csv');
    }
  }
}
</script>